import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus,
  Trash2,
  Upload,
  ImagePlus,
  Pencil,
  Check,
  X,
  Images,
  RefreshCcw,
  ArrowLeft,
  Star,
  Loader2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import AdminBreadcrumb from '@/components/AdminBreadcrumb';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GalleryAdminPage() {
  const navigate = useNavigate();
  const { albumId } = useParams();
  const [searchParams] = useSearchParams();
  const tab = searchParams.get('tab') || 'albums';
  const [albums, setAlbums] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const activeAlbum = albumId ? albums.find((a) => a.id === albumId) || null : null;

  const fetchAlbums = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/gallery/albums`);
      setAlbums(Array.isArray(data) ? data : []);
    } catch {
      toast.error('Failed to load albums');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAlbums();
  }, []);

  // ---- Create new album
  const handleCreate = async (payload) => {
    try {
      const { data } = await axios.post(`${API}/admin/gallery/albums`, payload);
      setAlbums((prev) => [...prev, { ...data, photo_count: 0 }]);
      setCreating(false);
      toast.success('Album created — drop in some photos');
      // URL-based navigation avoids a state/render race against the Radix
      // dialog teardown that occasionally left the user on the list view.
      navigate(`/admin/gallery/${data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not create album');
    }
  };

  // ---- Album updates ----
  const handleAlbumUpdate = (updated) => {
    setAlbums((prev) => prev.map((a) => (a.id === updated.id ? updated : a)));
  };

  const handleAlbumDelete = async (album) => {
    if (
      !window.confirm(
        `Delete the album "${album.title}" and all ${album.photo_count} photo(s)? This cannot be undone.`,
      )
    ) {
      return;
    }
    try {
      await axios.delete(`${API}/admin/gallery/albums/${album.id}`);
      setAlbums((prev) => prev.filter((a) => a.id !== album.id));
      if (albumId === album.id) navigate('/admin/gallery');
      toast.success('Album deleted');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not delete album');
    }
  };

  // ---- Render ----
  return (
    <div className="pt-28 pb-16 min-h-screen bg-[#F5F5F0]" data-testid="gallery-admin-page">
      <div className="container-custom px-6 md:px-12 lg:px-24 max-w-6xl mx-auto">
        <AdminBreadcrumb current="Gallery" />

        {tab === 'submissions' ? (
          <SubmissionsView onApproved={fetchAlbums} />
        ) : !activeAlbum ? (
          <AlbumsListView
            albums={albums}
            loading={loading}
            onCreate={() => setCreating(true)}
            onOpen={(id) => navigate(`/admin/gallery/${id}`)}
            onDelete={handleAlbumDelete}
            onRefresh={fetchAlbums}
          />
        ) : (
          <AlbumDetailView
            album={activeAlbum}
            onBack={() => {
              navigate('/admin/gallery');
              fetchAlbums();
            }}
            onAlbumUpdate={handleAlbumUpdate}
            onAlbumDelete={() => handleAlbumDelete(activeAlbum)}
          />
        )}
      </div>

      {creating && (
        <CreateAlbumDialog
          open
          onClose={() => setCreating(false)}
          onSubmit={handleCreate}
        />
      )}
    </div>
  );
}

/* ───────── Albums list ───────── */
function AlbumsListView({ albums, loading, onCreate, onOpen, onDelete, onRefresh }) {
  return (
    <>
      <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
        <div>
          <p className="text-caption text-[#84A98C] mb-2">Admin</p>
          <h1 className="heading-display text-3xl md:text-4xl text-[#354F52]">Gallery</h1>
          <p className="text-[#52796F] mt-2 text-sm max-w-2xl">
            Custom albums of studio moments, market days, finished pieces, and anything
            else you want to share. Photos here appear on the public{' '}
            <Link to="/gallery" className="underline decoration-[#D4AF37]/40" target="_blank" rel="noreferrer">
              /gallery
            </Link>{' '}
            page.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            onClick={onRefresh}
            variant="outline"
            className="flex items-center gap-2"
            data-testid="gallery-refresh"
          >
            <RefreshCcw className="w-4 h-4" strokeWidth={1.5} />
            Refresh
          </Button>
          <Button
            type="button"
            onClick={onCreate}
            className="bg-[#354F52] hover:bg-[#52796F] text-[#FAF9F6] flex items-center gap-2"
            data-testid="gallery-create-album"
          >
            <Plus className="w-4 h-4" strokeWidth={1.5} />
            New album
          </Button>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-[#84A98C]">Loading albums…</p>
      ) : albums.length === 0 ? (
        <div className="bg-white p-10 text-center rounded-sm border border-[#CAD2C5]/40">
          <Images className="w-10 h-10 text-[#84A98C] mx-auto mb-3" strokeWidth={1.25} />
          <p className="text-[#354F52] font-serif text-xl mb-2">No albums yet</p>
          <p className="text-[#52796F] text-sm max-w-md mx-auto mb-5">
            Create your first album — could be &ldquo;Studio Moments&rdquo;, &ldquo;Markets&rdquo;,
            &ldquo;Customer photos&rdquo;, or anything you like.
          </p>
          <Button
            type="button"
            onClick={onCreate}
            className="bg-[#354F52] hover:bg-[#52796F] text-[#FAF9F6]"
          >
            <Plus className="w-4 h-4 mr-2" /> Create your first album
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="albums-list">
          {albums.map((album) => (
            <div
              key={album.id}
              className="group relative bg-white rounded-sm border border-[#CAD2C5]/40 overflow-hidden hover:border-[#D4AF37]/40 transition-colors"
              data-testid={`album-card-${album.slug}`}
            >
              <button
                type="button"
                onClick={() => onOpen(album.id)}
                className="block text-left w-full"
              >
                <div className="aspect-[4/5] bg-[#F5F5F0] relative">
                  {album.cover_image ? (
                    <img
                      src={album.cover_image}
                      alt={album.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Images className="w-10 h-10 text-[#84A98C]" strokeWidth={1.25} />
                    </div>
                  )}
                  <div className="absolute top-3 left-3 bg-[#FAF9F6]/95 px-2.5 py-1 rounded-full text-[10px] tracking-widest uppercase text-[#354F52]">
                    {album.photo_count} photo{album.photo_count === 1 ? '' : 's'}
                  </div>
                </div>
                <div className="p-4">
                  <p className="font-serif text-lg text-[#354F52] leading-tight">
                    {album.title}
                  </p>
                  {album.description && (
                    <p className="text-xs text-[#52796F] mt-1 line-clamp-2 leading-relaxed">
                      {album.description}
                    </p>
                  )}
                </div>
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(album);
                }}
                className="absolute top-3 right-3 p-2 bg-[#FAF9F6]/95 rounded-full text-[#BC4749] hover:bg-white opacity-0 group-hover:opacity-100 transition-opacity"
                title="Delete album"
                data-testid={`album-delete-${album.slug}`}
              >
                <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
              </button>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

/* ───────── Album detail view (manage photos) ───────── */
function AlbumDetailView({ album, onBack, onAlbumUpdate, onAlbumDelete }) {
  const [photos, setPhotos] = useState([]);
  const [loadingPhotos, setLoadingPhotos] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const fileInputRef = useRef(null);
  const [editingMeta, setEditingMeta] = useState(false);
  const [metaDraft, setMetaDraft] = useState({
    title: album.title,
    description: album.description || '',
  });

  // Fetch photos
  const fetchPhotos = async () => {
    setLoadingPhotos(true);
    try {
      const { data } = await axios.get(`${API}/gallery/albums/${album.slug}`);
      setPhotos(data.photos || []);
    } catch {
      toast.error('Failed to load photos');
    } finally {
      setLoadingPhotos(false);
    }
  };

  useEffect(() => {
    fetchPhotos();
  }, [album.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- Upload ----
  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    let success = 0;
    let firstUploaded = null;
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append('file', file);
        const { data } = await axios.post(
          `${API}/admin/gallery/albums/${album.id}/upload`,
          fd,
          {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 60000,
          },
        );
        setPhotos((prev) => [...prev, data]);
        if (!firstUploaded) firstUploaded = data;
        success += 1;
      } catch (e) {
        toast.error(
          `${file.name}: ${e?.response?.data?.detail || 'upload failed'}`,
        );
      }
    }
    setUploading(false);
    if (success > 0) {
      toast.success(
        `Uploaded ${success} photo${success === 1 ? '' : 's'}`,
      );
      // Bump local album state
      onAlbumUpdate({
        ...album,
        photo_count: album.photo_count + success,
        cover_image: album.cover_image || firstUploaded?.image_url || null,
      });
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // ---- Photo update / delete ----
  const updatePhoto = async (photo, patch) => {
    try {
      const { data } = await axios.put(`${API}/admin/gallery/photos/${photo.id}`, patch);
      setPhotos((prev) => prev.map((p) => (p.id === photo.id ? data : p)));
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update photo');
      return false;
    }
  };

  const deletePhoto = async (photo) => {
    if (!window.confirm('Delete this photo? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/admin/gallery/photos/${photo.id}`);
      setPhotos((prev) => prev.filter((p) => p.id !== photo.id));
      const wasCover = album.cover_image === photo.image_url;
      onAlbumUpdate({
        ...album,
        photo_count: Math.max(0, album.photo_count - 1),
        cover_image: wasCover
          ? photos.find((p) => p.id !== photo.id)?.image_url || null
          : album.cover_image,
      });
      toast.success('Photo deleted');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not delete photo');
    }
  };

  const setAsCover = async (photo) => {
    try {
      const { data } = await axios.put(`${API}/admin/gallery/albums/${album.id}`, {
        cover_image: photo.image_url,
      });
      onAlbumUpdate(data);
      toast.success('Cover photo updated');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not set cover');
    }
  };

  // ---- Album meta save ----
  const saveMeta = async () => {
    try {
      const { data } = await axios.put(`${API}/admin/gallery/albums/${album.id}`, {
        title: metaDraft.title.trim(),
        description: metaDraft.description.trim(),
      });
      onAlbumUpdate(data);
      setEditingMeta(false);
      toast.success('Album updated');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save');
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center gap-2 text-[#84A98C] hover:text-[#354F52] transition-colors mb-6"
        data-testid="album-back"
      >
        <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
        <span className="text-sm uppercase tracking-widest">All albums</span>
      </button>

      <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
        <div className="flex-1 min-w-0">
          {editingMeta ? (
            <div className="space-y-3" data-testid="album-meta-form">
              <Input
                value={metaDraft.title}
                onChange={(e) => setMetaDraft({ ...metaDraft, title: e.target.value })}
                className="text-2xl font-serif"
                placeholder="Album title"
              />
              <Textarea
                value={metaDraft.description}
                onChange={(e) =>
                  setMetaDraft({ ...metaDraft, description: e.target.value })
                }
                placeholder="A short description (optional)"
                rows={2}
                maxLength={400}
              />
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  onClick={saveMeta}
                  className="bg-[#354F52] hover:bg-[#52796F] text-[#FAF9F6]"
                  data-testid="album-meta-save"
                >
                  <Check className="w-4 h-4 mr-1.5" /> Save
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setEditingMeta(false);
                    setMetaDraft({ title: album.title, description: album.description || '' });
                  }}
                >
                  <X className="w-4 h-4 mr-1.5" /> Cancel
                </Button>
              </div>
            </div>
          ) : (
            <>
              <p className="text-caption text-[#84A98C] mb-2">Album · /{album.slug}</p>
              <h1 className="heading-display text-3xl md:text-4xl text-[#354F52] mb-2">
                {album.title}
              </h1>
              {album.description && (
                <p className="text-[#52796F] text-sm max-w-2xl leading-relaxed">
                  {album.description}
                </p>
              )}
              <p className="text-xs text-[#84A98C] mt-2">
                {album.photo_count} photo{album.photo_count === 1 ? '' : 's'} · public at{' '}
                <Link
                  to={`/gallery/${album.slug}`}
                  target="_blank"
                  rel="noreferrer"
                  className="underline decoration-[#D4AF37]/40"
                >
                  /gallery/{album.slug}
                </Link>
              </p>
            </>
          )}
        </div>
        {!editingMeta && (
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setEditingMeta(true)}
              data-testid="album-edit-meta"
            >
              <Pencil className="w-4 h-4 mr-1.5" /> Edit
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={onAlbumDelete}
              className="text-[#BC4749] hover:bg-[#BC4749]/10"
            >
              <Trash2 className="w-4 h-4 mr-1.5" /> Delete album
            </Button>
          </div>
        )}
      </div>

      {/* Upload zone */}
      <div className="bg-white border-2 border-dashed border-[#CAD2C5] rounded-sm p-6 mb-8">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <p className="font-serif text-[#354F52] text-lg">Add photos</p>
            <p className="text-xs text-[#84A98C] mt-1">
              JPG, PNG or WebP · up to 8 MB each · drop multiple at once
            </p>
          </div>
          <Button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="bg-[#354F52] hover:bg-[#52796F] text-[#FAF9F6]"
            data-testid="gallery-upload-btn"
          >
            {uploading ? (
              <>
                <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Uploading…
              </>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> Upload photos
              </>
            )}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/webp"
            multiple
            className="hidden"
            data-testid="album-file-input"
            onChange={(e) => handleFiles(Array.from(e.target.files || []))}
          />
        </div>
      </div>

      {/* Drag-and-drop batch upload zone */}
      <div
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsDraggingOver(true);
        }}
        onDragEnter={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsDraggingOver(true);
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsDraggingOver(false);
        }}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsDraggingOver(false);
          const files = Array.from(e.dataTransfer?.files || []).filter((f) =>
            f.type.startsWith('image/'),
          );
          if (files.length === 0) {
            toast.error('Please drop image files only (JPG/PNG/WebP)');
            return;
          }
          handleFiles(files);
        }}
        onClick={() => !uploading && fileInputRef.current?.click()}
        className={`mb-4 border-2 border-dashed rounded-sm py-8 px-4 text-center cursor-pointer transition-colors ${
          isDraggingOver
            ? 'border-[#D4AF37] bg-[#FAF9F6]'
            : 'border-[#CAD2C5]/60 hover:border-[#84A98C] bg-white/40'
        } ${uploading ? 'opacity-60 pointer-events-none' : ''}`}
        data-testid="gallery-dropzone"
      >
        {uploading ? (
          <>
            <Loader2 className="w-8 h-8 text-[#84A98C] mx-auto mb-2 animate-spin" />
            <p className="text-sm text-[#354F52] font-medium">Uploading — hold on…</p>
          </>
        ) : (
          <>
            <Upload className="w-8 h-8 text-[#84A98C] mx-auto mb-2" strokeWidth={1.25} />
            <p className="text-sm text-[#354F52] font-medium">
              {isDraggingOver
                ? 'Drop the photos to upload'
                : 'Drag & drop photos here, or click to browse'}
            </p>
            <p className="text-xs text-[#84A98C] mt-1">
              JPG · PNG · WebP · up to 25 MB each — server resizes them automatically
            </p>
          </>
        )}
      </div>

      {loadingPhotos ? (
        <p className="text-sm text-[#84A98C]">Loading photos…</p>
      ) : photos.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-sm border border-[#CAD2C5]/40">
          <ImagePlus className="w-8 h-8 text-[#84A98C] mx-auto mb-2" strokeWidth={1.25} />
          <p className="text-[#52796F] text-sm">No photos yet — drop some in above.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="album-photos-grid">
          {photos.map((photo) => (
            <PhotoCard
              key={photo.id}
              photo={photo}
              isCover={album.cover_image === photo.image_url}
              onUpdate={(patch) => updatePhoto(photo, patch)}
              onDelete={() => deletePhoto(photo)}
              onSetCover={() => setAsCover(photo)}
            />
          ))}
        </div>
      )}
    </>
  );
}

/* ───────── One photo card ───────── */
function PhotoCard({ photo, isCover, onUpdate, onDelete, onSetCover }) {
  const [editing, setEditing] = useState(false);
  const [caption, setCaption] = useState(photo.caption || '');
  const [alt, setAlt] = useState(photo.alt_text || '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    const ok = await onUpdate({ caption, alt_text: alt });
    setSaving(false);
    if (ok) setEditing(false);
  };

  return (
    <div
      className="bg-white rounded-sm border border-[#CAD2C5]/40 overflow-hidden"
      data-testid={`photo-card-${photo.id}`}
    >
      <div className="relative aspect-square bg-[#F5F5F0]">
        <img
          src={photo.image_url}
          alt={photo.alt_text || 'gallery photo'}
          className="w-full h-full object-cover"
        />
        {isCover && (
          <div className="absolute top-2 left-2 bg-[#D4AF37] text-[#FAF9F6] text-[10px] tracking-widest uppercase px-2 py-1 rounded-full flex items-center gap-1">
            <Star className="w-3 h-3" fill="currentColor" strokeWidth={1} />
            Cover
          </div>
        )}
        <div className="absolute top-2 right-2 flex items-center gap-1">
          {!isCover && (
            <button
              type="button"
              onClick={onSetCover}
              className="p-1.5 bg-[#FAF9F6]/95 rounded-full text-[#52796F] hover:text-[#D4AF37]"
              title="Set as cover"
              data-testid={`photo-cover-${photo.id}`}
            >
              <Star className="w-3.5 h-3.5" strokeWidth={1.5} />
            </button>
          )}
          <button
            type="button"
            onClick={onDelete}
            className="p-1.5 bg-[#FAF9F6]/95 rounded-full text-[#BC4749] hover:bg-white"
            title="Delete photo"
            data-testid={`photo-delete-${photo.id}`}
          >
            <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
          </button>
        </div>
      </div>
      <div className="p-3 space-y-2">
        {editing ? (
          <>
            <Input
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="Caption (optional)"
              maxLength={240}
              className="text-sm"
              data-testid={`photo-caption-input-${photo.id}`}
            />
            <Input
              value={alt}
              onChange={(e) => setAlt(e.target.value)}
              placeholder="Alt text (for screen readers)"
              maxLength={240}
              className="text-xs"
              data-testid={`photo-alt-input-${photo.id}`}
            />
            <div className="flex items-center gap-2 pt-1">
              <Button
                type="button"
                size="sm"
                onClick={handleSave}
                disabled={saving}
                className="bg-[#354F52] hover:bg-[#52796F] text-[#FAF9F6] flex-1"
                data-testid={`photo-save-${photo.id}`}
              >
                <Check className="w-3 h-3 mr-1" />
                {saving ? 'Saving…' : 'Save'}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => {
                  setEditing(false);
                  setCaption(photo.caption || '');
                  setAlt(photo.alt_text || '');
                }}
              >
                <X className="w-3 h-3" />
              </Button>
            </div>
          </>
        ) : (
          <>
            <p className="text-xs text-[#354F52] font-serif italic min-h-[1.25rem] line-clamp-2">
              {photo.caption || (
                <span className="text-[#84A98C] not-italic">No caption</span>
              )}
            </p>
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="text-[10px] tracking-widest uppercase text-[#84A98C] hover:text-[#354F52] flex items-center gap-1"
              data-testid={`photo-edit-${photo.id}`}
            >
              <Pencil className="w-3 h-3" /> Edit
            </button>
          </>
        )}
      </div>
    </div>
  );
}

/* ───────── Create album dialog ───────── */
function CreateAlbumDialog({ open, onClose, onSubmit }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const submit = (e) => {
    e.preventDefault();
    if (title.trim().length < 2) return;
    onSubmit({ title: title.trim(), description: description.trim() || undefined });
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#FAF9F6]" data-testid="create-album-dialog">
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle className="font-serif text-[#354F52]">New album</DialogTitle>
            <DialogDescription className="text-[#52796F]">
              Give your album a name. You can add photos and a cover in a moment.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-caption text-[#84A98C] block mb-2">
                Album title
              </label>
              <Input
                autoFocus
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Studio Moments"
                maxLength={80}
                data-testid="new-album-title"
              />
            </div>
            <div>
              <label className="text-caption text-[#84A98C] block mb-2">
                Description (optional)
              </label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="A quiet line about what this album holds"
                rows={2}
                maxLength={400}
                data-testid="new-album-description"
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={title.trim().length < 2}
              className="bg-[#354F52] hover:bg-[#52796F] text-[#FAF9F6]"
              data-testid="new-album-submit"
            >
              <Plus className="w-4 h-4 mr-1.5" /> Create album
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* ───────── Customer photo submissions (UGC moderation) ───────── */
function SubmissionsView({ onApproved }) {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('pending');
  const [loading, setLoading] = useState(true);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/gallery/submissions`, {
        params: { status: filter === 'all' ? undefined : filter, limit: 200 },
      });
      setItems(Array.isArray(data) ? data : []);
    } catch {
      toast.error('Failed to load submissions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const approve = async (sub) => {
    try {
      await axios.post(`${API}/admin/gallery/submissions/${sub.id}/approve`);
      toast.success(`${sub.submitter_name}'s photo is now public`);
      setItems((prev) => prev.filter((s) => s.id !== sub.id));
      onApproved && onApproved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not approve');
    }
  };

  const reject = async (sub) => {
    if (!window.confirm(`Reject ${sub.submitter_name}'s photo? The file will be removed.`)) return;
    try {
      await axios.post(`${API}/admin/gallery/submissions/${sub.id}/reject`);
      setItems((prev) => prev.filter((s) => s.id !== sub.id));
      toast.success('Submission rejected');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not reject');
    }
  };

  return (
    <div data-testid="submissions-view">
      <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
        <div>
          <p className="text-caption text-[#84A98C] mb-2">Admin · Customer photos</p>
          <h1 className="heading-display text-3xl md:text-4xl text-[#354F52]">Submissions</h1>
          <p className="text-[#52796F] mt-2 text-sm max-w-2xl">
            Photos sent in by customers wearing their bracelets. Approve and they land in the
            <em> Customer Photos</em> album on /gallery automatically.
          </p>
        </div>
        <Link to="/admin/gallery" className="text-sm uppercase tracking-widest text-[#84A98C] hover:text-[#354F52]">
          ← All albums
        </Link>
      </div>

      <div className="flex items-center gap-2 mb-6">
        {['pending', 'approved', 'rejected', 'all'].map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setFilter(s)}
            className={`px-3 py-1.5 text-xs uppercase tracking-widest rounded-full transition-colors ${
              filter === s
                ? 'bg-[#354F52] text-[#FAF9F6]'
                : 'bg-white text-[#52796F] border border-[#CAD2C5]'
            }`}
            data-testid={`submissions-filter-${s}`}
          >
            {s}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-sm text-[#84A98C]">Loading…</p>
      ) : items.length === 0 ? (
        <div className="bg-white p-10 text-center rounded-sm border border-[#CAD2C5]/40">
          <Inbox className="w-8 h-8 text-[#84A98C] mx-auto mb-3" strokeWidth={1.25} />
          <p className="text-[#52796F]">No {filter === 'all' ? '' : filter} submissions right now.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="submissions-grid">
          {items.map((s) => (
            <div key={s.id} className="bg-white rounded-sm border border-[#CAD2C5]/40 overflow-hidden" data-testid={`submission-${s.id}`}>
              <div className="aspect-square bg-[#F5F5F0]">
                <img src={s.image_url} alt={`From ${s.submitter_name}`} className="w-full h-full object-cover" />
              </div>
              <div className="p-4">
                <p className="font-serif text-[#354F52] text-lg">{s.submitter_name}</p>
                <p className="text-xs text-[#84A98C] truncate">{s.submitter_email}</p>
                {s.bracelet_word && (
                  <p className="text-[10px] tracking-widest uppercase text-[#D4AF37] mt-1">
                    Word · {s.bracelet_word}
                  </p>
                )}
                {s.caption && (
                  <p className="text-sm text-[#52796F] mt-2 italic font-serif line-clamp-3">
                    “{s.caption}”
                  </p>
                )}
                {s.status === 'pending' && (
                  <div className="flex items-center gap-2 mt-4">
                    <Button type="button" onClick={() => approve(s)}
                            className="bg-[#354F52] hover:bg-[#52796F] text-[#FAF9F6] flex-1"
                            data-testid={`submission-approve-${s.id}`}>
                      <Check className="w-4 h-4 mr-1.5" /> Approve
                    </Button>
                    <Button type="button" variant="outline" onClick={() => reject(s)}
                            className="text-[#BC4749] border-[#BC4749]/40 hover:bg-[#BC4749]/10"
                            data-testid={`submission-reject-${s.id}`}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                )}
                {s.status !== 'pending' && (
                  <p className="text-xs text-[#84A98C] mt-3 uppercase tracking-widest">
                    {s.status}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

